import React from "react"

import { isBefore, startOfDay } from "date-fns"
import { CalendarIcon, ChevronDown, Download, Filter, Loader2, Search } from "lucide-react"
import { DateRange } from "react-day-picker"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { useAuth } from "@/contexts/AuthContext"
import { formatDayMonth, formatDayMonthYear } from "@/lib/dateFormats"
import { cn } from "@/lib/utils"
import { ROUTE_KIND_LABELS, ROUTE_KINDS, RouteKind } from "@/types/collections"

import RetiredRoutesButton from "../RetiredRoutesButton"
import SubcontractorsButton from "../SubcontractorsButton"

interface PlanningListFilterBarProps {
  searchTerm: string
  onSearchChange: (term: string) => void
  dateRange: DateRange | undefined
  onDateRangeChange: (range: DateRange | undefined) => void
  statusFilter: string[]
  onStatusFilterChange: (statuses: string[]) => void
  /** Empty means every kind. */
  kindFilter: RouteKind[]
  onKindFilterChange: (kinds: RouteKind[]) => void
  onExpandAll: () => void
  onCollapseAll: () => void
  routeCount: number
  haulageRouteCount: number
  runListRouteCount: number
  /** Day figures still arriving; exports wait for them. */
  exportsLoading: boolean
  onExport: () => void
  onExportHaulage: () => void
  onExportRunList: () => void
}

const PlanningListFilterBar: React.FC<PlanningListFilterBarProps> = ({
  searchTerm,
  onSearchChange,
  dateRange,
  onDateRangeChange,
  statusFilter,
  onStatusFilterChange,
  kindFilter,
  onKindFilterChange,
  onExpandAll,
  onCollapseAll,
  routeCount,
  haulageRouteCount,
  runListRouteCount,
  exportsLoading,
  onExport,
  onExportHaulage,
  onExportRunList,
}) => {
  const { can } = useAuth()
  return (
    <div className="bg-white border-b border-palette-jungleGreen200 p-3 flex-shrink-0">
      <div className="flex items-center gap-3">
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-palette-jungleGreen400" />
          <Input
            placeholder="Search routes..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9 bg-palette-jungleGreen000 border-palette-jungleGreen200 h-8 text-sm"
          />
        </div>

        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-8 bg-white border-palette-jungleGreen200 text-palette-jungleGreen700"
            >
              <CalendarIcon size={14} className="mr-2" />
              {dateRange?.from ? (
                dateRange.to ? (
                  <>
                    {formatDayMonth(dateRange.from)} - {formatDayMonthYear(dateRange.to)}
                  </>
                ) : (
                  formatDayMonthYear(dateRange.from)
                )
              ) : (
                "Select date range"
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent variant="plain" align="start">
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={onDateRangeChange}
              disabled={(date) => isBefore(date, startOfDay(new Date()))}
              numberOfMonths={2}
              className="bg-white"
              classNames={{
                day: "p-0 rounded-md hover:bg-amber-100",
                today: "text-amber-600 bg-amber-50",
                selected: "bg-amber-500 text-white hover:bg-amber-600",
                range_start: "bg-amber-500 text-white rounded-l-md hover:bg-amber-600",
                range_end: "bg-amber-500 text-white rounded-r-md hover:bg-amber-600",
                range_middle: "bg-amber-100 text-amber-900",
                chevron: "text-amber-600",
              }}
            />
          </PopoverContent>
        </Popover>

        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                "h-8 bg-white border-palette-jungleGreen200 text-palette-jungleGreen700",
                statusFilter.length > 0 && "bg-amber-50 border-amber-200",
              )}
            >
              <Filter size={14} className="mr-2" />
              Status {statusFilter.length > 0 && `(${statusFilter.length})`}
            </Button>
          </PopoverTrigger>
          <PopoverContent variant="menu" align="start">
            <div className="space-y-1">
              {["draft", "live", "cancelled"].map((status) => (
                <label
                  key={status}
                  className="flex items-center space-x-2 p-1.5 rounded hover:bg-palette-jungleGreen000 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={statusFilter.includes(status)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onStatusFilterChange([...statusFilter, status])
                      } else {
                        onStatusFilterChange(statusFilter.filter((s) => s !== status))
                      }
                    }}
                    className="w-3.5 h-3.5 text-amber-600 border-palette-jungleGreen300 rounded accent-amber-500"
                  />
                  <span className="text-xs text-palette-jungleGreen900">
                    {status === "live" ? "Live" : status === "draft" ? "Draft" : "Cancelled"}
                  </span>
                </label>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {can.planFilters && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "h-8 bg-white border-palette-jungleGreen200 text-palette-jungleGreen700",
                  kindFilter.length > 0 && "bg-slate-100 border-slate-300",
                )}
              >
                <Filter size={14} className="mr-2" />
                Kind {kindFilter.length > 0 && `(${kindFilter.length})`}
              </Button>
            </PopoverTrigger>
            <PopoverContent variant="menu" align="start">
              <div className="space-y-1">
                {ROUTE_KINDS.map((kind) => (
                  <label
                    key={kind}
                    className="flex items-center space-x-2 p-1.5 rounded hover:bg-palette-jungleGreen000 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={kindFilter.includes(kind)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          onKindFilterChange([...kindFilter, kind])
                        } else {
                          onKindFilterChange(kindFilter.filter((k) => k !== kind))
                        }
                      }}
                      className="w-3.5 h-3.5 border-palette-jungleGreen300 rounded accent-slate-500"
                    />
                    <span className="text-xs text-palette-jungleGreen900">
                      {ROUTE_KIND_LABELS[kind]}
                    </span>
                  </label>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        )}

        {can.editRoutes && (
          <>
            <RetiredRoutesButton />
            <SubcontractorsButton />
          </>
        )}

        <div className="flex-1" />

        <Button variant="ghost" size="sm" onClick={onExpandAll} className="h-8 text-xs">
          Expand All
        </Button>
        <Button variant="ghost" size="sm" onClick={onCollapseAll} className="h-8 text-xs">
          Collapse All
        </Button>

        {/* The haulage plan is HIVED-only; plan and run list are cut to what each login sees. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-8 bg-white border-palette-jungleGreen200"
              disabled={routeCount === 0 || exportsLoading}
            >
              {exportsLoading ? (
                <Loader2 size={14} className="mr-2 animate-spin" aria-label="Loading" />
              ) : (
                <Download size={14} className="mr-2" />
              )}
              Export
              <ChevronDown size={14} className="ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            <DropdownMenuItem onClick={onExport} className="flex-col items-start gap-0.5">
              <span className="text-xs font-medium">Full plan ({routeCount})</span>
              <span className="text-[10px] text-palette-jungleGreen500">
                {can.costs
                  ? "One row per route, with costs and load factors"
                  : "One row per route, with times, stops and volume"}
              </span>
            </DropdownMenuItem>
            {can.costs && (
              <DropdownMenuItem
                onClick={onExportHaulage}
                disabled={haulageRouteCount === 0}
                className="flex-col items-start gap-0.5"
              >
                <span className="text-xs font-medium">
                  Haulage stops &amp; timings ({haulageRouteCount})
                </span>
                <span className="text-[10px] text-palette-jungleGreen500">
                  A cell per stop, sites and times, no costs
                </span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={onExportRunList}
              disabled={runListRouteCount === 0}
              className="flex-col items-start gap-0.5"
            >
              <span className="text-xs font-medium">Run list PDF ({runListRouteCount})</span>
              <span className="text-[10px] text-palette-jungleGreen500">
                {runListRouteCount === 0 && routeCount > 0
                  ? "Nothing to list: every route here is cancelled"
                  : "Each route's stops in order, times and volume, no costs"}
              </span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}

export default PlanningListFilterBar
